import { useStore } from '@tanstack/react-form'
import { useQuery } from '@tanstack/react-query'
import { DataPolicyColumnEffect, LakehouseConnectionTypeEnum } from '@vendia/management-api-types'
import { FetchLakehouseDataPreviewDocument } from '@vendia/control-plane-api'
import clsx from 'clsx'
import dayjs from 'dayjs'
import debug from 'debug'
import { useNavigate } from 'react-router'
import Card, { CardRule } from 'src/components/containers/card'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { ProductPolicies } from 'src/components/lakehouse/product-policies'
import PageLoader from 'src/components/loaders/page-loader'
import { BasicTable } from 'src/components/tables/basic-table'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { transformJoinTableColumns } from 'src/utils/lakehouse/column-mapping'
import { LakehouseFlowStepValues, LakehouseTableForm } from 'src/utils/lakehouse/types'

import { snowflakeToAwsRegionMap } from '../utils'

const logger = debug('app:reviewSharingPermissions')

const processCsvPreviewData = (csvConfig: any, parsedCsvData: any, parsedCsvFields: any) => {
  const columnNames = Object.keys(csvConfig.columns)
  const columnTypes = csvConfig.columns
  const errorHandling = csvConfig.options.errorHandling
  const dateFormat = csvConfig.options.dateFormat || 'auto'
  const timeFormat = csvConfig.options.timeFormat || 'auto'
  const timestampFormat = csvConfig.options.timestampFormat || 'auto'

  function formatValue(value: string, type: string) {
    if (value == null) return ''
    if (type === 'DATE' && dateFormat !== 'auto') {
      if (dateFormat === 'yyyy-mm-dd') {
        const d = dayjs(value)
        if (d.isValid()) return d.format('YYYY-MM-DD')
      } else if (dateFormat === 'mm/dd/yyyy') {
        const d = dayjs(value)
        if (d.isValid()) return d.format('MM/DD/YYYY')
      }
    }
    if (type === 'TIME' && timeFormat !== 'auto') {
      if (timeFormat === 'hh:mm:ss') {
        const d = dayjs(value, ['HH:mm:ss', 'HH:mm'])
        if (d.isValid()) return d.format('HH:mm:ss')
      } else if (timeFormat === 'hh:mm') {
        const d = dayjs(value, ['HH:mm:ss', 'HH:mm'])
        if (d.isValid()) return d.format('HH:mm')
      }
    }
    if (type === 'TIMESTAMP' && timestampFormat !== 'auto') {
      if (timestampFormat === 'iso8601') {
        const d = dayjs(value)
        if (d.isValid()) return d.toISOString()
      }
    }
    return value
  }

  let rows = []
  for (const row of parsedCsvData) {
    let hasError = false
    const processedRow = columnNames.map((col, idx) => {
      let val
      const origKey = parsedCsvFields[idx]
      val = row[origKey]

      if (typeof val === 'string' && csvConfig.options.whitespace === 'trim') {
        val = val.trim()
      }
      val = formatValue(val, columnTypes[col])
      if (columnTypes[col] === 'FLOAT' && val !== '' && isNaN(Number(val))) {
        hasError = true
      }
      return val
    })
    if (hasError && errorHandling === 'skip') continue
    rows.push(processedRow)
    if (hasError && errorHandling === 'stop') break
  }
  return {
    columnHeaders: columnNames,
    rows,
  }
}

export const StepReviewSharingPermissions: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const client = useControlPlaneApi()
  const navigate = useNavigate()
  const form = context.form

  const {
    source,
    product,
    uniName,
    nodeName,
    selectedTableColumns,
    flowSteps,
    currStepIndex,
    currStep,
    connectionId,
    joinTableKey,
    keyColumns,
    csvConfig,
    parsedCsvData,
    parsedCsvFields,
  } = useStore(form.store, (state) => state.values)
  const { columnPolicies, defaultColumnPolicyEffect } = product
  const allExcluded =
    csvConfig ||
    (columnPolicies.length === selectedTableColumns.length &&
      columnPolicies?.every((p) => p.effect === DataPolicyColumnEffect.Exclude)) ||
    (columnPolicies.length === 0 && defaultColumnPolicyEffect === DataPolicyColumnEffect.Exclude)

  const { data: apiData, isLoading } = useQuery({
    queryKey: ['fetchLakehouseDataPreview', { source, selectedTableColumns, product, joinTableKey }],
    queryFn: async () => {
      const fetchDataInput = {
        connectionId,
        joinTableKey,
        uniName: uniName!,
        nodeName: nodeName!,
        clouderaConnection: source.cloudera ? source.cloudera : undefined,
        snowflakeConnection: source.snowflake
          ? {
              ...source.snowflake,
              role: source.snowflake?.role || '',
              schema: source.snowflake?.schema || '',
              authType: source.snowflake?.authType,
            }
          : undefined,
        postgresConnection: source.postgres ? source.postgres : undefined,
        mysqlConnection: source.mysql ? source.mysql : undefined,
        databricksConnection: source.databricks ? source.databricks : undefined,
        bigqueryConnection: source.bigquery ? source.bigquery : undefined,
        sourceTableDefinition: {
          columns: product.joinSql ? transformJoinTableColumns(selectedTableColumns) : selectedTableColumns,
          tableName: source.selectedTable,
          keyColumns: keyColumns,
        },
        tableDefinition: {
          columnPolicies: product.columnPolicies,
          defaultColumnPolicyEffect: product.defaultColumnPolicyEffect,
          rowPolicies: product.rowPolicies,
        },
        joinQuery: product.joinSql,
      }
      const response = await client.graphqlFetch({ query: FetchLakehouseDataPreviewDocument, variables: fetchDataInput })
      let rawRows: any[] = response?.fetchLakehouseDataPreview ?? []
      const columnHeaders = Object.keys(rawRows?.[0] ?? {})
      const rows =
        rawRows?.map((row: { [key: string]: any }) => {
          // map over headers to ensure order and handle missing values
          return columnHeaders.map((header) => {
            const value = row[header]
            if (value === null || value === undefined) return ''
            if (typeof value === 'object') return JSON.stringify(value)
            if (typeof value === 'boolean') return value.toString()
            return value
          })
        }) ?? []
      return {
        columnHeaders,
        rows,
      }
    },
    enabled: !allExcluded,
  })

  let data: { columnHeaders: string[]; rows: any[][] } | undefined = apiData
  if (csvConfig) {
    data = processCsvPreviewData(csvConfig, parsedCsvData, parsedCsvFields)
  }

  const { columnHeaders, rows } = data ?? { columnHeaders: [], rows: [] }

  logger('state', {
    source,
    product,
    selectedTableColumns,
    flowSteps,
    currStepIndex,
    currStep,
  })

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Review table settings'
        description='Take a moment to verify the filtering rules and table preview'
      />
      <ScrollableStepContent inset>
        <div className='w-full'>
          <div className='flex max-h-full w-full gap-6 px-8'>
            {source ? (
              <div className='w-1/3 min-w-[450px] max-w-[600px]'>
                <Card className='mb-6 w-full'>
                  <div className='flex flex-col gap-4'>
                    <div className='flex flex-col gap-2 pb-2'>
                      <div className='flex flex-col gap-4'>
                        <h5 className='text-neutral-9 text-xs font-bold'>Data source</h5>
                        {source.connection === LakehouseConnectionTypeEnum.Snowflake && (
                          <div className='flex items-center gap-4 truncate'>
                            <img
                              src='/images/vendor/snowflake-primary.png'
                              alt='Snowflake logo'
                              className='size-12 rounded-lg border border-slate-200'
                            />
                            <div className='flex flex-col gap-0.5'>
                              <div className='font-bold'>Snowflake</div>
                              <div className='text-neutral-8 text-sm'>{product.syncSchedule.replace(/_/g, '-')}</div>
                              <div className='text-neutral-8 text-sm'>
                                {snowflakeToAwsRegionMap[source.sourceRegion]}
                              </div>
                              <div className='text-neutral-8 text-sm'>{source.selectedTable}</div>
                            </div>
                          </div>
                        )}
                        {source.connection === LakehouseConnectionTypeEnum.Cloudera && (
                          <div className='flex items-center gap-4'>
                            <img
                              src='/images/vendor/cloudera-primary.png'
                              alt='Cloudera logo'
                              className='size-12 rounded-lg border border-slate-200'
                            />
                            <div className='flex flex-col gap-0.5'>
                              <div className='font-bold'>Cloudera</div>
                              <div className='text-neutral-8 text-xs'>{source.selectedTable}</div>
                            </div>
                          </div>
                        )}
                        {source.connection === LakehouseConnectionTypeEnum.Postgres && (
                          <div className='flex items-center gap-4'>
                            <img
                              src='/images/vendor/postgres-primary.png'
                              alt='Postgres logo'
                              className='size-12 rounded-lg border border-slate-200'
                            />
                            <div className='flex flex-col gap-0.5'>
                              <div className='font-bold'>Postgres</div>
                              <div className='text-neutral-8 text-xs'>{source.selectedTable}</div>
                            </div>
                          </div>
                        )}
                        {source.connection === LakehouseConnectionTypeEnum.Upload && (
                          <div className='flex items-center gap-4'>
                            <img
                              src='/images/vendor/upload-primary.png'
                              alt='file upload logo'
                              className='size-12 rounded-lg border border-slate-200'
                            />
                            <div className='flex flex-col gap-0.5'>
                              <div className='font-bold'>File Upload</div>
                              <div className='text-neutral-8 text-xs'>{csvConfig?.fileName}</div>
                            </div>
                          </div>
                        )}
                        {source.connection === LakehouseConnectionTypeEnum.S3 && (
                          <div className='flex items-center gap-4'>
                            <img
                              src='/images/vendor/s3-primary.png'
                              alt='s3 logo'
                              className='size-12 rounded-lg border border-slate-200'
                            />
                            <div className='flex flex-col gap-0.5'>
                              <div className='font-bold'>S3 Upload</div>
                              <div
                                className='text-neutral-8 max-w-[300px] truncate text-xs'
                                title={csvConfig?.fileName}
                              >
                                {csvConfig?.fileName.split('/').pop()}
                              </div>
                            </div>
                          </div>
                        )}
                        {source.connection === LakehouseConnectionTypeEnum.Databricks && (
                          <div className='flex items-center gap-4'>
                            <img
                              src='/images/vendor/databricks-primary.png'
                              alt='Databricks logo'
                              className='size-12 rounded-lg border border-slate-200'
                            />
                            <div className='flex flex-col gap-0.5'>
                              <div className='font-bold'>Databricks</div>
                              <div className='text-neutral-8 text-xs'>{source.selectedTable}</div>
                            </div>
                          </div>
                        )}
						{source.connection === LakehouseConnectionTypeEnum.Bigquery && (
                          <div className='flex items-center gap-4'>
                            <img
                              src='/images/vendor/bigquery-primary.png'
                              alt='BigQuery logo'
                              className='size-12 rounded-lg border border-slate-200'
                            />
                            <div className='flex flex-col gap-0.5'>
                              <div className='font-bold'>BigQuery</div>
                              <div className='text-neutral-8 text-xs'>{source.selectedTable}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
                <DataProductCard table={product} isFile={!!csvConfig} />
              </div>
            ) : null}
            <div className={clsx('flex w-2/3 min-w-0 flex-col gap-4', source ? 'flex-1' : 'w-full')}>
              <Card className='w-full' padding='none' contentExpandVertical>
                {isLoading ? (
                  <PageLoader />
                ) : rows.length === 0 || (allExcluded && !csvConfig) ? (
                  <div className='bg-red-100 p-8 text-red-600'>
                    <div className='h4'>Empty Table</div>
                    <div className='text-base'>
                      It looks like there would be no data in this table. Please review filtering rules.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='p-6'>
                      <div className='flex flex-col gap-1'>
                        <div className='text-lg font-bold'>Table Preview</div>
                        <div className='text-neutral-10 text-sm'>
                          Here is a sample of data from your selected {csvConfig ? 'file' : 'table'} with the chosen{' '}
                          {csvConfig ? 'options' : 'data filtering rules'}. To make changes, return to the previous
                          steps and update the {csvConfig ? 'options' : 'data filtering rules'}.
                        </div>
                      </div>
                    </div>
                    <BasicTable columnHeaders={columnHeaders} rows={rows} />
                  </>
                )}
              </Card>
            </div>
          </div>
        </div>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.isSubmitting]}>
        {([isSubmitting]) => (
          <StepButtons
            context={context}
            isLastStep
            isSubmitting={isSubmitting}
            hasCancel={true}
            onCancel={() => navigate(`/uni/${uniName}/${nodeName}/data`)}
            onNext={context.handleSubmit}
            nextBlocked={isLoading || rows.length === 0} // Prevent app creation if table would be empty
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}

const DataProductCard = ({ table, isFile }: { table: LakehouseTableForm; isFile: boolean }) => {
  return (
    <Card className='w-full' padding='none'>
      <div className='p-6'>
        <div className='h4'>{table.name}</div>
      </div>
      <CardRule />
      {isFile ? (
        <div className='space-y-6 bg-slate-50 p-6 text-sm'>
          {' '}
          <h6 className='h6 mb-2'>All file data included</h6>
        </div>
      ) : (
        <ProductPolicies table={table} />
      )}
    </Card>
  )
}
